import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Mail, Lock, LogIn, Loader2 } from 'lucide-react';

// Google Icon Component
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" className="mr-2">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const LoginPage = () => {
  const navigate = useNavigate();
  const { signIn, signInWithGoogle } = useAuth();
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  // Helper function to get user-friendly error messages
  const getLoginErrorMessage = (error) => {
    // Supabase returns error info in different ways
    const errorMessage = (error?.message || error?.msg || '').toLowerCase();
    const errorCode = error?.code || error?.error_code || error?.status;
    // Invalid credentials (wrong password or email not found)
    if (errorMessage.includes('invalid login credentials') || 
        errorMessage.includes('invalid_credentials') ||
        errorCode === 'invalid_credentials' ||
        errorCode === 400) {
      return 'Incorrect email or password. Please check your credentials and try again.';
    }
    
    // Email not confirmed
    if (errorMessage.includes('email not confirmed') || 
        errorCode === 'email_not_confirmed') {
      return 'Please verify your email address before signing in. Check your inbox for the verification link.';
    }
    
    // Too many requests
    if (errorMessage.includes('too many requests') || 
        errorCode === 'over_request_rate_limit' ||
        errorCode === 429) {
      return 'Too many login attempts. Please wait a few minutes and try again.';
    }
    
    // User not found
    if (errorMessage.includes('user not found')) {
      return 'No account found with this email. Please check your email or create a new account.';
    }
    
    // Network or server errors
    if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
      return 'Unable to connect. Please check your internet connection and try again.';
    }
    
    // Default fallback - still informative
    return 'Incorrect email or password. Please check your credentials and try again.';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const { data, error } = await signIn(formData.email, formData.password);
      
      if (error) {
        const friendlyMessage = getLoginErrorMessage(error);
        toast.error(friendlyMessage, {
          duration: 5000,
          description: error.message?.includes('credentials') ? 
            'Forgot your password? Click the link below to reset it.' : undefined
        });
      } else {
        toast.success('Welcome back!');
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('Login error:', err);
      toast.error('Incorrect email or password. Please check your credentials and try again.', {
        duration: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    try {
      const { error } = await signInWithGoogle();
      if (error) {
        toast.error(error.message || 'Failed to sign in with Google');
        setGoogleLoading(false);
      }
      // Don't set loading to false on success - redirect will happen
    } catch (err) {
      console.error('Google login error:', err);
      toast.error('Failed to sign in with Google');
      setGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8F9FA] via-[#EDF6F9] to-[#E2E8F0] flex items-center justify-center py-12 px-6" data-testid="login-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-xl p-8 md:p-12 max-w-md w-full"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-[#003B5C] to-[#74A57F] rounded-xl mx-auto mb-4 flex items-center justify-center">
            <LogIn className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-[#003B5C] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Welcome Back
          </h1>
          <p className="text-slate-600">Sign in to your Lucent Sky ESG portal</p>
        </div>

        {/* Google Sign In Button */}
        <Button
          type="button"
          variant="outline"
          onClick={handleGoogleSignIn}
          disabled={googleLoading || loading}
          className="w-full h-12 mb-6 border-slate-300 hover:bg-slate-50 text-slate-700 font-medium"
          data-testid="google-signin-button"
        >
          {googleLoading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Connecting...
            </>
          ) : (
            <>
              <GoogleIcon />
              Continue with Google
            </>
          )}
        </Button>

        {/* Divider */}
        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-slate-200"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-white text-slate-500">or sign in with email</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="email" className="text-[#003B5C] mb-2 block">
              Email Address
            </Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                id="email"
                type="email"
                placeholder="you@company.com.au"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="pl-10 h-12"
                required
                data-testid="login-email-input"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="password" className="text-[#003B5C] mb-2 block">
              Password
            </Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="pl-10 h-12"
                required
                data-testid="login-password-input"
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {/* Remember me checkbox could go here */}
            </div>
            <Link to="/forgot-password" className="text-sm text-[#003B5C] hover:underline">
              Forgot password?
            </Link>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#003B5C] text-white hover:bg-[#002A42] rounded-full h-12 text-lg font-medium"
            data-testid="login-submit-button"
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-slate-600">
            Don't have an account?{' '}
            <Link to="/signup" className="text-[#003B5C] font-medium hover:underline">
              Create Account
            </Link>
          </p>
        </div>

        <div className="mt-6 pt-6 border-t border-slate-200 text-center">
          <Link to="/" className="text-sm text-slate-600 hover:text-[#003B5C]">
            ← Back to Home
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;